import { queryKeys } from '@/lib/query-keys';
// @deploy 2026-06-11 — retrigger preview for PR #454
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppRuntimeConfigProvider } from '@/components/app-runtime-config-provider';
import { PostHogProvider } from '@/components/posthog-provider';
import { TelemetryBootstrap } from '@/components/telemetry-bootstrap';
import { AppVersionGate } from '@/features/app-version';
import { forgetActiveLocation } from '@/features/organization-locations/use-active-location';
import { rehydrateAuthToken } from '@/lib/auth-token';
import {
  recheckSubscriptionAfterBillingError,
  registerBillingRecoveryHandler,
} from '@/lib/billing-recovery';
import { installCapgoFailureReporting, notifyCapgoAppReady } from '@/lib/capgo';
import {
  clearChunkReloadGuard,
  installChunkReloadHandler,
} from '@/lib/chunk-reload';
import { configureNativeViewport } from '@/lib/configure-native-viewport';
import { installDeepLinkListener } from '@/lib/deep-links';
import { installGlobalErrorHandlers } from '@/lib/global-error-handlers';
import { logError } from '@/lib/log-error';
import { queryClient } from '@/lib/query-client';
import { sessionQueryOptions } from '@/lib/session';
import { registerSessionInvalidationHandler } from '@/lib/session-lifecycle';
import {
  connectBuildRefreshToRouter,
  installWebBuildRefresh,
} from '@/lib/web-build-refresh';
import { router } from '@/router';

import '@/styles.css';

// FIRST, before anything else can throw. PostHog's own exception autocapture
// arms itself from a lazily-fetched script well after init, so unhandled errors
// during boot used to reach Sentry only. These listeners buffer into
// log-error.ts and replay once posthog-js is ready.
installGlobalErrorHandlers();

if (Capacitor.isNativePlatform()) {
  configureNativeViewport();
  document.documentElement.classList.add('native-app');
  if (Capacitor.getPlatform() === 'ios') {
    document.documentElement.classList.add('native-ios');
  }
}

// Tell Capgo the new JS bundle launched successfully so it doesn't roll back
// to the previous one. Must run early; if the app crashes before this fires,
// Capgo restores the prior bundle on next launch.
if (Capacitor.isNativePlatform()) {
  void notifyCapgoAppReady();
  // Register BEFORE any update check can fire, so a download/apply failure on
  // this launch is reported rather than lost. A failed OTA is otherwise
  // silent — the app falls back to the baked bundle and boots normally.
  installCapgoFailureReporting();
  // Emit which bundle actually booted, on a fixed single-line prefix.
  //
  // This is the assertion target for the OTA gate. The E2E APK is built with
  // CAP_DEFAULT_CHANNEL set, so when Capgo serves the bundle the app runs the
  // OTA'd JS — but if the download or apply FAILS, it silently falls back to
  // the copy baked into the APK. That fallback is built from the same source,
  // so every Maestro flow would still pass and the gate would be theatre.
  // apps/app/scripts/maestro-android.sh greps logcat for this line and fails
  // when the version is not the one CI just uploaded.
  //
  // Deliberately console.log rather than the app logger: it has to survive in
  // a release-profile WebView with no transport configured, and reach logcat
  // through Capacitor's console bridge.
  void CapacitorUpdater.current()
    .then(({ bundle }) => {
      console.log(`[capgo-boot] version=${bundle.version} id=${bundle.id}`);
    })
    .catch(() => {
      console.log('[capgo-boot] version=unknown id=unknown');
    });
}

installDeepLinkListener();

// An API-proven expired token is a normal authentication transition, not an
// exception for whichever protected query happened to run first. Clear its
// cached identity and re-run the route guard so it sends the user to sign-in.
registerSessionInvalidationHandler((loss) => {
  // A missing credential only ends a session this tab actually had. A page that
  // never signed in (or already lost its session) must not be bounced.
  if (
    loss === 'missing' &&
    !queryClient.getQueryData(sessionQueryOptions.queryKey)?.user
  ) {
    return false;
  }
  queryClient.clear();
  // Whoever signs in next may belong to another org — see forgetActiveLocation.
  forgetActiveLocation();
  void router.navigate({ to: '/sign-in' });
  return true;
});

registerBillingRecoveryHandler((error) => {
  const session = queryClient.getQueryData(sessionQueryOptions.queryKey);
  // Ignore responses from requests belonging to an account we have left.
  if (error.details?.organizationId !== session?.user?.organizationId) return;
  // Leave the user on the affected page. The shared banner and plain error
  // explanation provide recovery without replacing their current screen.
  recheckSubscriptionAfterBillingError(
    queryClient,
    queryKeys.billing.subscription()
  );
});

// Auto-recover from stale dynamic-import / chunk-load failures after a deploy
// (e.g. "Load failed" on a hashed `/assets/index-*.js`). Must run before the
// router mounts so the first lazy route load is covered.
installChunkReloadHandler();

// Once a new deploy is live, the user's next page change loads it (the root
// route's `beforeLoad` does the hand-off), instead of the tab staying on the
// previous build until someone refreshes by hand.
installWebBuildRefresh({ isBusy: () => queryClient.isMutating() > 0 });
// The router's navigation events are the ONLY reliable way to tell a real
// navigation from a hover preload inside the root `beforeLoad`.
connectBuildRefreshToRouter(router);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Restore the persisted bearer before mounting — the first `auth/session`
// query inside `_authed.beforeLoad` needs it, otherwise cold-launch always
// redirects to /sign-in.
void rehydrateAuthToken().finally(() => {
  // Boot reached the render path successfully — reset the reload guard so a
  // future stale-deploy in this same tab can recover again.
  clearChunkReloadGuard();
  createRoot(rootElement, {
    onUncaughtError(error, errorInfo) {
      logError('react.uncaughtError', error, {
        feature: 'react',
        extra: { componentStack: errorInfo.componentStack },
      });
    },
    onCaughtError(error, errorInfo) {
      logError('react.caughtError', error, {
        feature: 'react',
        extra: { componentStack: errorInfo.componentStack },
      });
    },
  }).render(
    <StrictMode>
      <AppRuntimeConfigProvider>
        <TelemetryBootstrap>
          <QueryClientProvider client={queryClient}>
            <PostHogProvider>
              <AppVersionGate>
                <RouterProvider router={router} />
              </AppVersionGate>
            </PostHogProvider>
          </QueryClientProvider>
        </TelemetryBootstrap>
      </AppRuntimeConfigProvider>
    </StrictMode>
  );
});

// preview rebuild trigger (ENG-367 ghost commit) — 2026-06-13
